import React from 'react';
import styled from 'styled-components';
import type { DataSourceEventPayload } from '@splunk/dashboard-types';
import P from '@splunk/react-ui/Paragraph';
import List from '@splunk/react-ui/List';
import { variables } from '@splunk/themes';
import getStatusMessage from './utils/getStatusMessage';
import { useMeasuredTextWidth } from './hooks';

const DefaultContentMinWidth = '200px';

type GenericPopoverContainerProps = {
    $minWidth: string;
};

const GenericPopoverContainer = styled.div<GenericPopoverContainerProps>`
    padding: ${variables.spacingMedium};
    min-width: ${(props) => `min(100vw, 500px, ${props.$minWidth})`};
    max-width: 500px;
    word-break: break-word;
`;

const StyledList = styled(List)`
    margin-inline-start: ${variables.spacingMedium};
    margin-block-start: ${variables.spacingMedium};
`;

const UnresolvedTokensPopoverContent = ({
    message,
    tokens,
}: {
    message?: string;
    tokens: string[];
}) => {
    const messageWidth = useMeasuredTextWidth(message, DefaultContentMinWidth);
    return (
        <GenericPopoverContainer $minWidth={messageWidth}>
            <P>{message}</P>
            <StyledList>
                {tokens.map((token) => (
                    <List.Item key={token}>{token}</List.Item>
                ))}
            </StyledList>
        </GenericPopoverContainer>
    );
};

export interface PopoverContentFactoryProps {
    dataSource?: DataSourceEventPayload;
    message?: string;
}

export const GenericPopover = ({ message }: { message?: string }) => {
    const messageWidth = useMeasuredTextWidth(message, DefaultContentMinWidth);

    return message ? (
        <GenericPopoverContainer $minWidth={messageWidth}>
            {message}
        </GenericPopoverContainer>
    ) : null;
};

const GenericPopoverContentFactory = ({
    dataSource,
    message,
}: PopoverContentFactoryProps) => {
    if (message) {
        return <GenericPopover message={message} />;
    }

    if (!dataSource) {
        return null;
    }

    const statusMessage = getStatusMessage(dataSource);

    if (
        dataSource.meta &&
        Array.isArray(dataSource.meta.missedTokens) &&
        dataSource.meta.missedTokens.length
    ) {
        return (
            <UnresolvedTokensPopoverContent
                message={statusMessage}
                tokens={dataSource.meta.missedTokens}
            />
        );
    }

    return <GenericPopover message={statusMessage} />;
};

export default GenericPopoverContentFactory;
